import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from "react";
import { getCSRFToken } from "../lib/csrf";

// User Type Definition
export interface User {
  id: number;
  name: string;
  email: string;
  username?: string;
  avatarUrl?: string; // Changed from avatar_url to avatarUrl
  initials: string;
  role: "user" | "super_admin";
  isSuperAdmin: boolean; // Changed from is_super_admin to isSuperAdmin
  planType?: string; // Added to match concern output
}

// Context Type
interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// Create Context
export const UserContext = createContext<UserContextType | null>(null);

// Provider Component
export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch current user from API
  const fetchUser = async () => {
    try {
      const csrfToken = getCSRFToken();
      const response = await fetch("/api/v1/users/current", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch user data.");

      const data = await response.json();
      setUser(data.user || null);
    } catch (error) {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      const csrfToken = getCSRFToken();
      await fetch("/logout", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
      });
    } catch (error) {
      console.error("⚠️ Logout error:", error);
    } finally {
      setUser(null);
      window.location.href = "/";
    }
  };

  // Fetch user on mount
  useEffect(() => {
    fetchUser();
  }, []);

  if (isLoading) {
    return <></>;
  }

  return (
    <UserContext.Provider
      value={{ user, setUser, refreshUser: fetchUser, logout }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Custom Hook
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("❌ useUser must be used within a <UserProvider>");
  }
  return context;
};
